import React from 'react'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/functions'
import { IEcommerceQuestionSchema } from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import style from './Header.module.scss'

interface IHeaderProps {
  selectedIndex: number
  totals: number[]
  schema: IEcommerceQuestionSchema
  findability: boolean
  showCart: boolean
  onCartOpen: () => void
  onCartClose: () => void
  onItemClose: () => void
  clearSelection: () => void
}

const Header = ({
  selectedIndex,
  totals,
  schema,
  findability,
  showCart,
  onCartOpen,
  onCartClose,
  onItemClose,
  clearSelection
}: IHeaderProps) => {
  const viewport = useViewport()
  const { t, languageTag } = useSurveyLocale()
  return (
    <>
      {viewport.mobile ? (
        <header className={style.headerMobile} data-testid="SW-EC-Header">
          <div className={style.topRow}>
            <button className={style.arrowBack} onClick={selectedIndex >= 0 ? onItemClose : onCartClose}>
              <i className="wm-ChevronLeft" />
            </button>
            <div className={style.input}>
              <EmptyLink color="#0053e2">
                <i className={`wm-Search ${style.searchIcon}`} />
              </EmptyLink>
              <div className={style.search} onClick={clearSelection}>
                {schema.search_condition || t('questions', 'ec', 'walmart', 'search')}
              </div>
              <EmptyLink color="#0053e2">
                <i className={`wm-Close ${style.closeIcon}`} />
              </EmptyLink>
            </div>
            {findability ? (
              <EmptyLink color="#0053e2">
                <div className={style.cart}>
                  <div className={style.cartIcon}>
                    <div
                      className={style.amount}
                      dangerouslySetInnerHTML={{ __html: `${totals[0] >= 100 ? '99+' : totals[0]}` }}
                    ></div>
                    <i className="wm-Cart" />
                  </div>
                  <div
                    className={style.sum}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                    }}
                  />
                </div>
              </EmptyLink>
            ) : (
              <button className={style.cart} onClick={onCartOpen}>
                <div className={style.cartIcon}>
                  <div
                    className={style.amount}
                    dangerouslySetInnerHTML={{ __html: `${totals[0] >= 100 ? '99+' : totals[0]}` }}
                  ></div>
                  <i className="wm-Cart" />
                </div>
                <span
                  className={style.sum}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                  }}
                />
              </button>
            )}
          </div>
          <div className={style.bottomRow}>
            <div className={style.delivery}>
              <div className={style.image}></div>
              <div className={style.get}>{t('questions', 'ec', 'walmart', 'howYouWant')}</div>
              <div className={style.zip}>{t('questions', 'ec', 'walmart', 'zip')}</div>
            </div>
            <EmptyLink color="#0053e2">
              <i className="wm-ChevronDown"></i>
            </EmptyLink>
          </div>
        </header>
      ) : (
        <header className={style.header} data-testid="SW-EC-Header">
          <div className={style.headerMain}>
            <div className={style.logo} onClick={showCart ? onCartClose : onItemClose} />
            <EmptyLink color="#0053e2">
              {!showCart ? (
                <div className={style.delivery}>
                  <div className={style.icon} />
                  <div className={style.center}>
                    <span className={style.text}>{t('questions', 'ec', 'walmart', 'howYouWant')}</span>
                    <span className={style.address}>{t('questions', 'ec', 'walmart', 'myAddress')}</span>
                  </div>
                  <div>
                    <i className={'wm-ChevronDown'} />
                  </div>
                </div>
              ) : null}
            </EmptyLink>
            <div className={style.input}>
              <div
                onClick={clearSelection}
                className={`${style.text} ${!schema.search_condition ? style.textDisabled : ''}`}
                dangerouslySetInnerHTML={{
                  __html: schema.search_condition || t('questions', 'ec', 'walmart', 'search')
                }}
              ></div>
              <EmptyLink color="#0053e2">
                <span className={style.search}>
                  <i className="pi pi-search" />
                </span>
              </EmptyLink>
            </div>
            <EmptyLink color="#0053e2">
              <div className={style.headerButton}>
                <div className={style.icon}>
                  <i className=" wm-Heart" />
                </div>
                <div>
                  <div className={style.topText}>{t('questions', 'ec', 'walmart', 'reorder')}</div>
                  <div className={style.bottomText}>{t('questions', 'ec', 'walmart', 'myItems')}</div>
                </div>
              </div>
            </EmptyLink>
            <EmptyLink color="#0053e2">
              <div className={style.headerButton}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" fill="currentColor">
                  <path d="M300 262q34 18 49.5 52t6.5 71t-38.5 60.5t-67.5 23.5t-67.5 -23.5t-38.5 -60.5t6.5 -71t49.5 -52q-40 -8 -71 -32.5t-49 -60t-17 -75.5v-31h31v31q0 38 19 70.5t51 51t70 18.5h32q38 0 70 -18.5t51 -51t19 -70.5v-31h-187v-32h187q13 0 22.5 9.5t9.5 21.5v32 q0 40 -18 75.5t-49 60t-71 32.5zM328 359q0 -32 -23 -55t-55 -23t-55 23t-23 55.5t23 55.5t55 23t55 -23t23 -56z"></path>
                </svg>
                <div>
                  <div className={style.topText}>{t('questions', 'ec', 'walmart', 'signIn')}</div>
                  <div className={style.bottomText}>{t('questions', 'ec', 'walmart', 'account')}</div>
                </div>
              </div>
            </EmptyLink>
            {findability ? (
              <EmptyLink color="#0053e2">
                <div className={style.cart}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" fill="currentColor">
                    <path
                      transform="translate(500, 500) rotate(180)"
                      d="M89 438h-58v-32h51l44 -145l30 9l-32 105h311l-24 -111l-249 -27q-15 -2 -25.5 -14.5t-9.5 -28.5t12.5 -27t27.5 -11h255v32h-255q-4 0 -6.5 2.5t-2.5 6t2 6t6 3.5l255 28q7 1 12 5.5t7 11.5l28 129q3 10 -4 18t-17 8h-332l-5 16q-2 7 -8 11t-13 4v1zM188 63q12 0 21.5 9t9.5 22t-9.5 22t-22 9t-22 -9t-9.5 -22t9.5 -22t22.5 -9zM391 63q13 0 22 9t9 22t-9 22t-22 9t-22.5 -9t-9.5 -22t9.5 -22t22.5 -9z"
                    />
                  </svg>
                  <span
                    className={style.totals}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                    }}
                  />
                  <span className={style.amount} dangerouslySetInnerHTML={{ __html: `${totals[0]}` }}></span>
                </div>
              </EmptyLink>
            ) : (
              <button className={style.cart} onClick={findability ? null : onCartOpen}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" fill="currentColor">
                  <path
                    transform="translate(500, 500) rotate(180)"
                    d="M89 438h-58v-32h51l44 -145l30 9l-32 105h311l-24 -111l-249 -27q-15 -2 -25.5 -14.5t-9.5 -28.5t12.5 -27t27.5 -11h255v32h-255q-4 0 -6.5 2.5t-2.5 6t2 6t6 3.5l255 28q7 1 12 5.5t7 11.5l28 129q3 10 -4 18t-17 8h-332l-5 16q-2 7 -8 11t-13 4v1zM188 63q12 0 21.5 9t9.5 22t-9.5 22t-22 9t-22 -9t-9.5 -22t9.5 -22t22.5 -9zM391 63q13 0 22 9t9 22t-9 22t-22 9t-22.5 -9t-9.5 -22t9.5 -22t22.5 -9z"
                  />
                </svg>
                <span
                  className={style.totals}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                  }}
                />
                <span className={style.amount} dangerouslySetInnerHTML={{ __html: `${totals[0]}` }}></span>
              </button>
            )}
          </div>
          <div className={style.headerFilters}>
            <div className={style.filters}>
              <EmptyLink color="#0053e2">
                <span className={style.control}>{t('questions', 'ec', 'walmart', 'department')}</span>
                <i className="wm-ChevronDown" />
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <span className={style.control}>{t('questions', 'ec', 'walmart', 'services')}</span>
                <i className="wm-ChevronDown" />
              </EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter1')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter2')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter3')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter4')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter5')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter6')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter7')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter8')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter9')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter10')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter11')}</EmptyLink>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'filter12')}</EmptyLink>
            </div>
          </div>
        </header>
      )}
    </>
  )
}

export default Header
